/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";

import { useDebouncedCallback } from "use-debounce";

import { type KeyOfOrString } from "../types/KeyOfOrString.types";

export interface IInputDebouncingContext {
  useDebouncing: boolean;
  inputDelay?: number;
  maxDelay?: number;
}

const DEBOUNCE_INPUT_DELAY = 300;
const MAX_DEBOUNCE_DELAY = 1000;

interface DebouncedOnChangeResult<T, FormValues> {
  /** The current display value for the input. */
  debouncedValue?: T | undefined;
  /** Debounced wrapper around `onChange`. */
  debouncedChange: (
    id: KeyOfOrString<FormValues>,
    value: any,
    ...rest: any[]
  ) => void;
  /** Immediately fires any pending debounced `onChange` call. */
  flush: () => void;
}

/**
 * This hook debounces the onChange callback. Used for storing a cached local state for a component.
 * This can substantially increase performance by only needing a rerender on the current component every change.
 * The parent component will be rerendered every {@link MAX_DEBOUNCE_DELAY} milliseconds
 * @param value the components outside value (from props)
 * @param onChange callback to debounce, the wrapped/debounced version will be returned as debouncedChange. Use this in place of calls to onChange
 * @param debouncingContext
 * @example
 * const { debouncedValue, debouncedChange, flush } = useDebouncedOnChange(value, onChange);
 * <BdsInput
 *     id={id}
 *     value={debouncedValue} // this will update every rerender
 *     onChange={(event) => debouncedChange(id, event.target.value)} // the call to this will be debounced
 *     onBlur={() => flush()} // ensures parent state is current before save
 * />
 */
export function useDebouncedOnChange<T, FormValues>(
  value: T,
  onChange: (id: any, value: any, ...restProps: any[]) => void,
  debouncingContext?: IInputDebouncingContext,
): DebouncedOnChangeResult<T, FormValues> {
  const [debouncedValue, setDebouncedValue] = useState<T | undefined | null>(
    null,
  );
  // Debounce callback
  const debounced = useDebouncedCallback(
    (id, value, ...rest) => {
      onChange(id, value, rest);
      setDebouncedValue(null);
    },
    (debouncingContext && debouncingContext.inputDelay) || DEBOUNCE_INPUT_DELAY,
    {
      maxWait:
        (debouncingContext && debouncingContext.maxDelay) ?? MAX_DEBOUNCE_DELAY,
    },
  );

  const debouncedChange = useCallback(
    function (
      id: KeyOfOrString<FormValues> | (KeyOfOrString<FormValues> & string),
      value: any,
      ...restProps: any[]
    ) {
      // do not debounce for unit tests or if context is set to not debounce
      if (debouncingContext && !debouncingContext.useDebouncing) {
        onChange(id, value, ...restProps);
      } else {
        setDebouncedValue(value);
        debounced.callback(id, value, ...restProps);
      }
    },
    [debounced, onChange, debouncingContext],
  );

  const flush = useCallback(() => {
    if (debounced.pending()) {
      debounced.flush();
    }
  }, [debounced]);

  useEffect(() => {
    // Check if debounced is currently pending
    // If it is, call flush() to manually execute the callback
    // Necessary for when a component will potentially unmount before the debounce finishes (ex: Line Item Containers)
    return () => {
      flush();
    };
  }, [flush]);

  if (debouncedValue === undefined) {
    // if debounced is undefined, it means the user actively is clearing
    // the associated field. It can't fall through to the normal return because that
    // would restore the value the user is trying to clear, so we need to explicitly
    // return only the debounced value (undefined).
    return { debouncedValue, debouncedChange, flush };
  }
  // if debouncedValue has a value, use that, otherwise fall back to
  // the last committed value
  return { debouncedValue: debouncedValue ?? value, debouncedChange, flush };
}
